import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Trash2, AlertTriangle, User } from 'lucide-react';

const RemoveConnectionModal = ({ isOpen, onClose, friend, onRemove }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleRemove = async () => {
    if (!friend) return;
    
    setLoading(true);
    setError('');
    
    try {
      await onRemove(friend.id);
      onClose();
    } catch (error) {
      console.error('Error removing connection:', error);
      setError('Failed to remove connection. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  if (!friend) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-red-600" />
            Remove Connection
          </DialogTitle>
        </DialogHeader>

        {error && (
          <Alert className="border-red-200 bg-red-50">
            <AlertDescription className="text-red-600">
              {error}
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          {/* Friend Info */}
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <div 
              className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold"
              style={{
                background: 'linear-gradient(135deg, var(--brand-navy) 0%, var(--brand-navy-light) 100%)'
              }}
            >
              {friend.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="font-medium" style={{color: 'var(--brand-navy)'}}>
                {friend.name}
              </div>
              {friend.company && (
                <div className="text-sm text-gray-600">{friend.company}</div>
              )}
              {friend.services && friend.services.length > 0 && (
                <div className="text-sm text-gray-500">
                  {friend.services.slice(0, 2).join(', ')}
                  {friend.services.length > 2 && ` +${friend.services.length - 2} more`}
                </div>
              )}
            </div>
          </div>

          {/* Warning */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-orange-600" />
              <span className="font-medium text-orange-800 text-sm">
                Are you sure you want to remove this connection?
              </span>
            </div>
            <p className="text-sm text-orange-600">
              This will remove <strong>{friend.name}</strong> from your network along with:
            </p>
            <ul className="text-sm text-orange-600 mt-2 ml-4 list-disc">
              <li>All reviews and ratings you've given them</li>
              <li>Plug relationship history and timeline</li>
              <li>Any referral links you've shared</li>
              <li>Connection history and analytics</li>
            </ul>
            <p className="text-sm text-orange-600 mt-2 font-medium">
              You can always add them back later, but the history will be lost.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleClose}
              className="flex-1"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleRemove}
              disabled={loading}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              {loading ? 'Removing...' : 'Remove Connection'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RemoveConnectionModal;